import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TournamentService } from '../../../core/services/tournament.service';
import { BranchService } from '../../../core/services/branch.service';
import { AvailableIndividual, CreateTeamFromIndividualsRequest, NewTeamMember } from '../../../core/models/create-team-from-individuals.model';
import { Branch } from '../../../core/models/branch.model';

@Component({
  selector: 'app-create-team-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './create-team-modal.html',
  styleUrl: './create-team-modal.scss'
})
export class CreateTeamModal implements OnInit {
  @Input() tournamentId: string = '';
  @Input() show: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() teamCreated = new EventEmitter<void>();

  availableIndividuals: AvailableIndividual[] = [];
  selectedIndividuals: Set<string> = new Set();
  newMembers: NewTeamMember[] = [];
  branches: Branch[] = [];

  isLoading: boolean = false;
  isSubmitting: boolean = false;
  errorMessage: string = '';

  teamName: string = '';
  jerseyColor: string = '';
  nearestBranch: string = '';
  captainId: string | null = null;

  searchQuery: string = '';
  filterAssigned: 'all' | 'unassigned' | 'assigned' = 'unassigned';

  constructor(
    private tournamentService: TournamentService,
    private branchService: BranchService
  ) {}

  ngOnInit() {
    if (this.show) {
      this.loadData();
    }
  }

  ngOnChanges() {
    if (this.show) {
      this.loadData();
    }
  }

  loadData() {
    this.loadAvailableIndividuals();
    this.loadBranches();
  }

  loadAvailableIndividuals() {
    this.isLoading = true;
    this.tournamentService.getAvailableIndividuals(this.tournamentId).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.success && response.data) {
          this.availableIndividuals = response.data;
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.errorMessage = 'Failed to load individuals';
        console.error(error);
      }
    });
  }

  loadBranches() {
    this.branchService.getBranches().subscribe({
      next: (response) => {
        if (response.success && response.data) {
          this.branches = response.data.filter(b => b.isActive);
        }
      },
      error: (error) => console.error('Error loading branches:', error)
    });
  }

  get filteredIndividuals(): AvailableIndividual[] {
    return this.availableIndividuals.filter(ind => {
      // Filter by assignment status
      if (this.filterAssigned === 'unassigned' && ind.isAssigned) return false;
      if (this.filterAssigned === 'assigned' && !ind.isAssigned) return false;

      // Filter by search query
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        return ind.fullName.toLowerCase().includes(query) ||
               ind.phoneNumber?.toLowerCase().includes(query) ||
               ind.skillLevel?.toLowerCase().includes(query);
      }

      return true;
    });
  }

  toggleIndividual(id: string) {
    if (this.selectedIndividuals.has(id)) {
      this.selectedIndividuals.delete(id);
      if (this.captainId === id) {
        this.captainId = null;
      }
    } else {
      this.selectedIndividuals.add(id);
    }
  }

  selectAllUnassigned() {
    this.filteredIndividuals
      .filter(ind => !ind.isAssigned)
      .forEach(ind => this.selectedIndividuals.add(ind.id));
  }

  clearSelection() {
    this.selectedIndividuals.clear();
    this.captainId = null;
  }

  addNewMember() {
    this.newMembers.push({
      fullName: '',
      gender: null,
      dateOfBirth: null,
      preferredPosition: null,
      skillLevel: '',
      phoneNumber: '',
      isCaptain: false
    });
  }

  removeNewMember(index: number) {
    this.newMembers.splice(index, 1);
  }

  get totalPlayers(): number {
    return this.selectedIndividuals.size + this.newMembers.length;
  }

  get canSubmit(): boolean {
    return this.teamName.trim() !== '' &&
           !this.isSubmitting;
  }

  submitTeam() {
    if (!this.canSubmit) return;

    this.isSubmitting = true;
    this.errorMessage = '';

    const request: CreateTeamFromIndividualsRequest = {
      tournamentId: this.tournamentId,
      teamName: this.teamName,
      jerseyColor: this.jerseyColor || null,
      nearestBranch: this.nearestBranch || null,
      individualRegistrationIds: Array.from(this.selectedIndividuals),
      newMembers: this.newMembers,
      captainIndividualRegistrationId: this.captainId
    };

    this.tournamentService.createTeamFromIndividuals(request).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        if (response.success) {
          this.teamCreated.emit();
          this.closeModal();
        }
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error.error?.message || 'Failed to create team';
        console.error(error);
      }
    });
  }

  closeModal() {
    this.close.emit();
    this.resetForm();
  }

  resetForm() {
    this.teamName = '';
    this.jerseyColor = '';
    this.nearestBranch = '';
    this.captainId = null;
    this.selectedIndividuals.clear();
    this.newMembers = [];
    this.searchQuery = '';
    this.errorMessage = '';
  }

  calculateAge(dob: string | null): number {
    if (!dob) return 0;
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }
}
